/**
 * MobileAlertsView — mobile-app-style alert list with summary bar + sections.
 *
 * Matches palcare_mobile_android AlertsScreen.kt structure:
 *   - Summary bar (Waiting / In Progress / Longest Wait)
 *   - "MY ACCEPTED" — alerts the session user has accepted (top)
 *   - "WAITING — NO RESPONSE" — unaccepted open alerts
 *   - "IN PROGRESS" — alerts accepted by other users
 *   - Longest-wait bubble at the top reflects the oldest truly-waiting alert
 *
 * Each section sorts oldest event_datetime_utc first so the longest-waiting
 * row sits at the top of its section.
 */

import { useEffect, useMemo, useState } from 'react';
import MobileAlertCard from './MobileAlertCard.jsx';
import '../../styles/mobile-alerts.css';

const formatMMSS = (ms) => {
    const total = Math.floor(ms / 1000);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    const pad = (n) => String(n).padStart(2, '0');
    if (h > 0) return `${h}:${pad(m)}:${pad(s)}`;
    return `${pad(m)}:${pad(s)}`;
};

const elapsedMs = (utcStr) => {
    if (!utcStr) return 0;
    const iso = utcStr.endsWith?.('Z') ? utcStr : `${utcStr}Z`;
    return Math.max(0, Date.now() - new Date(iso).getTime());
};

const MobileAlertsView = ({
    alerts,
    sessionUserId,
    sessionUsername,
    onAccept,
    onFinish,
    pendingEventId,
    highPriorityTypeIds = new Set(),
}) => {
    const [, setTick] = useState(0);
    useEffect(() => {
        const t = setInterval(() => setTick((n) => n + 1), 1000);
        return () => clearInterval(t);
    }, []);

    // Show everything the caller hands us — mirrors CloudAlertsPage.jsx which
    // shows alerts until the context itself drops them (including
    // Cleared-but-unaccepted rows that linger on the page).
    const active = useMemo(() => [...(alerts || [])], [alerts]);

    const isMine = (a) => (
        (a.accepted_user_id != null && sessionUserId != null && String(a.accepted_user_id) === String(sessionUserId))
        || (a.accepted_username && sessionUsername && String(a.accepted_username) === String(sessionUsername))
    );

    const isHighPriority = (a) => highPriorityTypeIds.has((a?.event_type_id ?? '').toString());

    // Oldest event_datetime_utc first — longest-waiting alert at the top.
    // Missing timestamps sink to the bottom.
    const byOldestEvent = (a, b) => {
        const ta = a.event_datetime_utc ? new Date(`${a.event_datetime_utc}${a.event_datetime_utc.endsWith?.('Z') ? '' : 'Z'}`).getTime() : Infinity;
        const tb = b.event_datetime_utc ? new Date(`${b.event_datetime_utc}${b.event_datetime_utc.endsWith?.('Z') ? '' : 'Z'}`).getTime() : Infinity;
        return ta - tb;
    };

    // High-priority alerts pin to the top of their bucket regardless of wait
    // time; within the high-priority and normal groups we still order oldest-first.
    const byPriorityThenOldest = (a, b) => {
        const ap = isHighPriority(a) ? 1 : 0;
        const bp = isHighPriority(b) ? 1 : 0;
        if (ap !== bp) return bp - ap;
        return byOldestEvent(a, b);
    };

    const waiting = useMemo(
        () => active.filter((a) => !a.accepted_datetime_utc && !a.accepted_display).sort(byPriorityThenOldest),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [active, highPriorityTypeIds],
    );
    const myAccepted = useMemo(
        () => active.filter((a) => (a.accepted_datetime_utc || a.accepted_display) && isMine(a)).sort(byPriorityThenOldest),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [active, sessionUserId, sessionUsername, highPriorityTypeIds],
    );
    const othersInProgress = useMemo(
        () => active.filter((a) => (a.accepted_datetime_utc || a.accepted_display) && !isMine(a)).sort(byPriorityThenOldest),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [active, sessionUserId, sessionUsername, highPriorityTypeIds],
    );
    const inProgressCount = myAccepted.length + othersInProgress.length;

    // Truly-waiting alerts drive the summary stats — unaccepted AND not ack'd.
    // A cleared-but-unaccepted alert is still displayed in the list below, but
    // it shouldn't inflate the Waiting count or the Longest Wait timer.
    const trulyWaiting = useMemo(
        () => waiting.filter((a) => !a.ack_datetime_utc),
        [waiting],
    );

    // Longest Wait = oldest truly-waiting alert. Recomputed every render so
    // the 1s tick keeps it live.
    let longestMs = 0;
    for (const a of trulyWaiting) {
        const ms = elapsedMs(a.event_datetime_utc);
        if (ms > longestMs) longestMs = ms;
    }

    const ORANGE_MS = 5 * 60 * 1000;
    const RED_MS = 10 * 60 * 1000;
    const longestColor = trulyWaiting.length === 0
        ? undefined
        : longestMs >= RED_MS ? 'var(--color-danger, #e63757)'
        : longestMs >= ORANGE_MS ? 'var(--color-warning-text, #d97706)'
        : undefined;

    return (
        <div className="mav-root">
            <div className="mav-summary">
                <div className="mav-summary__title">Active Alerts</div>
                <div className="mav-summary__stats">
                    <div className="mav-stat">
                        <div className="mav-stat__n">{trulyWaiting.length}</div>
                        <div className="mav-stat__l">Waiting</div>
                    </div>
                    <div className="mav-stat">
                        <div className="mav-stat__n">{inProgressCount}</div>
                        <div className="mav-stat__l">In Progress</div>
                    </div>
                    <div className="mav-stat">
                        <div className="mav-stat__n" style={longestColor ? { color: longestColor } : undefined}>
                            {trulyWaiting.length === 0 ? '—' : formatMMSS(longestMs)}
                        </div>
                        <div className="mav-stat__l">Longest Wait</div>
                    </div>
                </div>
            </div>

            <div className="mav-list">
                {active.length === 0 ? (
                    <div className="mav-empty">
                        <div className="mav-empty__icon">{'\u2713'}</div>
                        <div className="mav-empty__text">No active alerts</div>
                    </div>
                ) : null}

                {myAccepted.length > 0 ? (
                    <>
                        <div className="mav-section-header mav-section-header--mine">MY ACCEPTED</div>
                        {myAccepted.map((row) => (
                            <MobileAlertCard
                                key={row.event_id}
                                row={row}
                                sessionUserId={sessionUserId}
                                sessionUsername={sessionUsername}
                                onAccept={onAccept}
                                onFinish={onFinish}
                                pending={pendingEventId === row.event_id}
                                highPriority={isHighPriority(row)}
                            />
                        ))}
                    </>
                ) : null}

                {waiting.length > 0 ? (
                    <>
                        <div className="mav-section-header mav-section-header--waiting">WAITING — NO RESPONSE</div>
                        {waiting.map((row) => (
                            <MobileAlertCard
                                key={row.event_id}
                                row={row}
                                sessionUserId={sessionUserId}
                                sessionUsername={sessionUsername}
                                onAccept={onAccept}
                                onFinish={onFinish}
                                pending={pendingEventId === row.event_id}
                                highPriority={isHighPriority(row)}
                            />
                        ))}
                    </>
                ) : null}

                {othersInProgress.length > 0 ? (
                    <>
                        <div className="mav-section-header">IN PROGRESS</div>
                        {othersInProgress.map((row) => (
                            <MobileAlertCard
                                key={row.event_id}
                                row={row}
                                sessionUserId={sessionUserId}
                                sessionUsername={sessionUsername}
                                onAccept={onAccept}
                                onFinish={onFinish}
                                pending={pendingEventId === row.event_id}
                                highPriority={isHighPriority(row)}
                            />
                        ))}
                    </>
                ) : null}
            </div>
        </div>
    );
};

export default MobileAlertsView;
